import { Box, ChipTypeMap, Tooltip, Typography } from '@mui/material';
import {
  GridActionsCellItem,
  GridCellParams,
  GridEnrichedColDef,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-pro';
import { ExportTypeEnum, ImportTypeEnum, TaskStatusEnum } from '@pxtra-client-lib-legacy/gen';
import { format } from 'date-fns/esm';
import { TFunction } from 'i18next';
import { FC } from 'react';
import { NavigateFunction } from 'react-router';
import { Chip } from '../../../../lib/components';
import { TableDataGridProps } from '../../../../lib/components/compound/tableDataGrid';
import {
  CsvIcon,
  ExcelIcon,
  HelpIcon,
  ImageIcon,
  PdfIcon,
  TxtIcon,
  ZipIcon
} from '../../../../lib/icons';
import { IconProps } from '../../../../lib/icons/templates/icon';
import Util from '../../../../misc/util';
import { TaskType } from '../types';
import { TooltipComponent } from '../../../../lib/components/atomic/feedback';

export type TableProps = Omit<TableDataGridProps, 'rows' | 'columns' | 'threepointMenu'>;
export interface ColumnDef<T> {
  id: string;
  name: string;
  type: T;
  fileType?: string;
  fileUrl?: string;
  fileName?: string;
  status?: TaskStatusEnum;
  errrorCount: number;
}

export type ImportColumnDef = ColumnDef<ImportTypeEnum> & { errorMessages?: string[] };
export type ExportColumnDef = ColumnDef<ExportTypeEnum> & { errorMessages?: string[] };

interface StatusInfo {
  lable: (count?: number) => string;
  color: ChipTypeMap['props']['color'];
}

type FileTypeEnum = 'pdf' | 'csv' | 'zip' | 'image' | 'xlsx' | 'personio' | 'txt';
type FileTypeIconList = Record<FileTypeEnum, FC<IconProps>>;
type Categories = {
  value: ImportTypeEnum | ExportTypeEnum;
  label: string;
};

const fileTypeIconList: FileTypeIconList = {
  pdf: PdfIcon,
  csv: CsvIcon,
  txt: TxtIcon,
  zip: ZipIcon,
  image: ImageIcon,
  xlsx: ExcelIcon,
  personio: ExcelIcon
};

const getFileTypeIcon = (type?: FileTypeEnum) => {
  return type && type in fileTypeIconList ? fileTypeIconList[type] : HelpIcon;
};

interface FileIconProps extends IconProps {
  type?: FileTypeEnum;
}

const FileTypeIcon: FC<FileIconProps> = ({ type, ...props }) => {
  const Icon = getFileTypeIcon(type);
  return <Icon {...props} />;
};

const buildSharedColumns = (t: TFunction, context: TaskType): Array<GridEnrichedColDef> => {
  const statusInfo: Record<string, StatusInfo> = {
    created: { lable: () => t('taskStatus:CREATED'), color: 'primary' },
    processing: { lable: () => t('taskStatus:PROCESSING'), color: 'warning' },
    finished: { lable: () => t('taskStatus:FINISHED'), color: 'success' },
    error: { lable: (count) => t('taskStatus:ERROR', { count: count }), color: 'error' }
  };

  const importCategories: Array<Categories> = [
    { value: 'employees', label: t('tasks:TYPE_employees') }
  ];

  const exportCategories: Array<Categories> = [
    { value: 'employee_data', label: t('tasks:TYPE_employee_data') },
    { value: 'payroll_data', label: t('tasks:TYPE_payroll_data') },
    { value: 'receipts', label: t('tasks:TYPE_receipts') },
    { value: 'voucher_orders', label: t('tasks:TYPE_voucher_orders') },
    { value: 'usc_memberships', label: t('tasks:TYPE_usc_memberships') }
  ];

  return [
    {
      field: 'fileType',
      headerName: t('tableTitle:FILE_TYPE'),
      sortable: false,
      width: 20,
      valueGetter: (params) => {
        return params.row.fileType;
      },
      renderHeader: () => '',
      renderCell: (params: GridCellParams) => (
        <TooltipComponent title={params.row.fileType} placement="top">
          <Box>
            <FileTypeIcon type={params.row.fileType} color="grey.500" />
          </Box>
        </TooltipComponent>
      )
    },
    {
      field: 'type',
      headerName: t('tableTitle:TYPE', { context: context }),
      width: 200,
      type: 'singleSelect',
      valueOptions: context === 'import' ? importCategories : exportCategories,
      renderCell: (params: GridRenderCellParams) => (
        <Typography noWrap>{t('tasks:TYPE', { context: params.value })}</Typography>
      )
    },
    {
      field: 'name',
      headerName: t('tableTitle:NAME', { context: context }),
      flex: 3,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value} enterDelay={1500}>
          <Typography noWrap>{params.value}</Typography>
        </Tooltip>
      )
    },
    {
      field: 'created_at',
      headerName: t('tableTitle:DATE'),
      type: 'date',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Typography>{format(params.value, 'dd.MM.yy')}</Typography>
      )
    },
    {
      field: 'time',
      headerName: t('tableTitle:TIME'),
      width: 90,
      renderCell: (params: GridRenderCellParams) => (
        <Typography>{format(params.value, 'HH:mm')}</Typography>
      )
    },
    { field: 'owner', headerName: t('tableTitle:OWNER', { context: context }), flex: 2 },
    {
      field: 'status',
      headerName: t('tableTitle:STATUS'),
      minWidth: 160,
      type: 'singleSelect',
      valueOptions: [
        { value: 'finished', label: t('taskStatus:FINISHED') },
        { value: 'error', label: t('taskStatus:ERROR') },
        { value: 'processing', label: t('taskStatus:PROCESSING') },
        { value: 'created', label: t('taskStatus:CREATED') }
      ],
      renderCell: (params: GridRenderCellParams) => {
        const errorMessages: string[] = Array.isArray(params.row.errorMessages)
          ? params.row.errorMessages
          : [];
        const chip = (
          <Chip
            label={statusInfo[params.value].lable(params.row.errrorCount)}
            color={statusInfo[params.value].color}
            disabled={params.value === 'created'}
          />
        );
        if (params.value !== 'error' || errorMessages.length === 0) {
          return chip;
        }
        const preview = errorMessages.slice(0, 5).join('\n\n');
        const suffix =
          errorMessages.length > 5
            ? `\n\n${t('taskStatus:ADDITIONAL_ERRORS', { count: errorMessages.length - 5 })}`
            : '';
        return (
          <Tooltip
            title={<Box className="whitespace-pre-line">{preview + suffix}</Box>}
            arrow
            placement="left"
          >
            <Box>{chip}</Box>
          </Tooltip>
        );
      }
    }
  ];
};

const buildDetailsActionItem = (
  params: GridRowParams,
  t: TFunction,
  navigate?: NavigateFunction
) => (
  <GridActionsCellItem
    label={t('common:DETAILS')}
    onClick={navigate ? () => navigate('/imports/' + params.row.id) : () => alert('DETAILS')}
    showInMenu
    key={`${params.row.id}-edit`}
    placeholder=""
  />
);

const buildDownloadActionItem = (params: GridRowParams, t: TFunction) => {
  return params.row.fileUrl ? (
    <GridActionsCellItem
      label={t('common:DOWNLOAD')}
      onClick={() => {
        Util.downloadFile(params.row.fileUrl, params.row.fileName);
      }}
      showInMenu
      key={`${params.row.id}-download`}
      placeholder=""
    />
  ) : (
    <></>
  );
};

const buildDeleteActionItem = (
  params: GridRowParams,
  t: TFunction,
  openDeleteDialog: (id: string) => void
) => {
  return params.row.status === 'error' ? (
    <GridActionsCellItem
      label={t('common:DELETE')}
      onClick={() => openDeleteDialog(params.row.id)}
      showInMenu={true}
      key={`${params.row.id}-delete`}
      placeholder=""
    />
  ) : (
    <></>
  );
};

export const buildExportColumns = (
  t: TFunction,
  openDeleteDialog: (id: string) => void
): Array<GridEnrichedColDef> => [
  ...buildSharedColumns(t, 'export'),
  {
    field: 'actions',
    type: 'actions',
    align: 'right',
    width: 50,
    getActions: (params: GridRowParams) => [
      buildDownloadActionItem(params, t),
      buildDeleteActionItem(params, t, openDeleteDialog)
    ]
  }
];

export const buildImportColumns = (
  t: TFunction,
  navigate: NavigateFunction
): Array<GridEnrichedColDef> => [
  ...buildSharedColumns(t, 'import'),
  {
    field: 'actions',
    type: 'actions',
    align: 'right',
    width: 50,
    getActions: (params: GridRowParams) => [
      buildDetailsActionItem(params, t, navigate),
      buildDownloadActionItem(params, t)
    ]
  }
];
